import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';

ChartJS.register(
    ArcElement,
    BarElement,
    CategoryScale,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip
);

/* Global defaults so every chart in the app reads as one system. */
ChartJS.defaults.font.family =
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
ChartJS.defaults.font.size = 11;
ChartJS.defaults.color = '#8a92a2';
ChartJS.defaults.animation.duration = 320;
ChartJS.defaults.animation.easing = 'easeOutQuart';

/**
 * Categorical palette. Deliberately short and desaturated — charts are
 * analytical instruments here, not decoration.
 */
export const SERIES = ['#0a2a66', '#2589c9', '#0f9d76', '#d98613', '#dc4c3f', '#7b8aa8'];

/** Semantic colours, used whenever a category carries a status meaning. */
export const SEMANTIC = {
    // task / project status
    todo: '#a7aebd',
    planned: '#a7aebd',
    open: '#a7aebd',
    in_progress: '#2589c9',
    active: '#2589c9',
    on_hold: '#d98613',
    pending: '#d98613',
    blocked: '#dc4c3f',
    cancelled: '#dc4c3f',
    overdue: '#dc4c3f',
    done: '#0f9d76',
    completed: '#0f9d76',
    closed: '#0f9d76',
    // priority
    low: '#a7aebd',
    medium: '#2589c9',
    high: '#d98613',
    critical: '#dc4c3f',
    urgent: '#dc4c3f',
};

export const colorFor = (key, index = 0) =>
    SEMANTIC[String(key || '').toLowerCase().replace(/\s+/g, '_')] || SERIES[index % SERIES.length];

export const TOOLTIP = {
    backgroundColor: '#1b2432',
    titleColor: '#ffffff',
    titleFont: { size: 11, weight: '600' },
    bodyColor: '#dbe0e8',
    bodyFont: { size: 11 },
    padding: 8,
    cornerRadius: 4,
    displayColors: true,
    boxWidth: 8,
    boxHeight: 8,
    boxPadding: 4,
    borderColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
};

const AXIS_TICK = { color: '#8a92a2', font: { size: 11 }, padding: 6 };

/** Cartesian defaults: one axis carries grid lines, never both. */
export const cartesian = ({ horizontal = false, stacked = false, maxTicks } = {}) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: TOOLTIP,
    },
    scales: {
        x: {
            stacked,
            grid: {
                display: horizontal,
                color: '#eceef2',
                drawTicks: false,
                drawBorder: false,
            },
            border: { display: false },
            ticks: { ...AXIS_TICK, maxTicksLimit: maxTicks },
        },
        y: {
            stacked,
            beginAtZero: true,
            grid: {
                display: !horizontal,
                color: '#eceef2',
                drawTicks: false,
                drawBorder: false,
            },
            border: { display: false },
            ticks: { ...AXIS_TICK, precision: 0, maxTicksLimit: 6 },
        },
    },
});

export const doughnut = () => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: TOOLTIP,
    },
});

export const humanize = (value) =>
    String(value || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

export default ChartJS;
